import React, { useMemo } from 'react';
import { Typography, Box, Card, Avatar, useMediaQuery } from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';

import PageTemplate from 'src/components/PageTemplate';
import { useExpertManagement } from 'src/hooks/useExpertManagement';
import BackButton from 'src/components/BackButton';
import { useDecodedToken } from 'src/hooks/useDecodedToken';
import Logo from 'src/components/Logo';

// Import our new components and hooks
import { useReportsData } from 'src/hooks/useReportsData';
import { useQueryFiltering } from 'src/hooks/useQueryFiltering';
import { formatCompanyDisplayName, formatCompanyForApi, generateCompanyInitials, getExternalEffortsForCompany } from 'src/utils/reportUtils';
import { useEffortsFiltering } from 'src/hooks/useEffortsFiltering';
import EffortsTable from 'src/pages/Reports/components/EffortsTable';
import NotesSection from 'src/pages/Reports/components/NotesSection';
import ChiefObjective from 'src/pages/Reports/components/ChiefObjective';

const ReportProfile = () => {
  const { companyID } = useParams();
  const [searchParams] = useSearchParams();
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const { experts } = useExpertManagement();
  const { decodedToken, isExpert } = useDecodedToken();
  const isMobile = useMediaQuery('(max-width: 600px)');

  // Custom hooks - pass dates to enable server-side filtering
  const { allQueries, allReports, isLoading, allReportsMutate } = useReportsData(startDate, endDate);

  const { getCompanyQueries, getQueriesByExpert } = useQueryFiltering(
    allQueries,
    experts,
    null, // Not needed for single company view
    'custom',
    { custom: { start: moment(startDate), end: moment(endDate) } },
    ''
  );

  // Memoized company information
  const displayCompanyName = useMemo(() => {
    const name = formatCompanyDisplayName(companyID);
    // Exceptions: ensure proper casing for specific company names
    const nameExceptions = {
      'Moosemoney': 'MooseMoney',
      'Seoplus+': 'seoplus+',
      'Talentlab': 'TalentLab',
    };
    return nameExceptions[name] || name;
  }, [companyID]);
  const apiCompanyName = useMemo(() => formatCompanyForApi(companyID), [companyID]);
  const companyInitials = useMemo(() => generateCompanyInitials(displayCompanyName), [displayCompanyName]);

  // Get company-specific queries and group by expert
  const companyQueries = useMemo(() => {
    return getCompanyQueries(companyID, startDate, endDate);
  }, [getCompanyQueries, companyID, startDate, endDate]);

  const queriesByExpert = useMemo(() => {
    return getQueriesByExpert(companyQueries);
  }, [getQueriesByExpert, companyQueries]);

  // Get external efforts for this company
  const externalEfforts = useMemo(() => {
    return getExternalEffortsForCompany(allReports, displayCompanyName, startDate, endDate);
  }, [allReports, displayCompanyName, startDate, endDate]);

  // Use the filtering hook to get pre-filtered and sorted data
  const { winsData, effortsData } = useEffortsFiltering(queriesByExpert, externalEfforts);

  return (
    <PageTemplate loading={isLoading} sx={{ mt: 6 }} loadingMessage="Loading takes about 15-30 seconds">
      {!isExpert && <BackButton />}
      <Card
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'white',
          borderRadius: 1,
          gap: 2,
          pb: 10,
          border: '1px solid #ddd',
          boxShadow: '0px 16px 32px 0px rgba(50, 63, 80, 0.24)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: '1px solid #ddd',
            p: 6,
            mb: 2,
            backgroundImage: 'url(/assets/bg-dots-dark.svg)',
            backgroundSize: 'cover',
            backgroundPosition: 'bottom',
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Logo to="/reports" width="250px" height="60px" src="/assets/logo-inverse.svg" />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 6 },
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar
              sx={{
                width: { xs: 64, sm: 80 },
                height: { xs: 64, sm: 80 },
                fontSize: { xs: '1.5rem', sm: '2rem' },
                fontWeight: 'bold',
              }}
            >
              {companyInitials}
            </Avatar>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, justifyContent: 'center' }}>
              <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                {displayCompanyName} Report
              </Typography>

              {isMobile && <ChiefObjective company={companyID.trim().replace(/-/g, '')} variant="inline" />}

              <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                {moment(startDate).format('MMM DD, YYYY')} - {moment(endDate).format('MMM DD, YYYY')}
              </Typography>
            </Box>
          </Box>

          {!isMobile && <ChiefObjective company={companyID.trim().replace(/-/g, '')} />}
        </Box>

        {/* Wins Table - Only show if there are wins */}
        {winsData.hasData && (
          <Box sx={{ mt: 2, px: { xs: 2, sm: 6 } }}>
            <Typography variant="h6" sx={{ mb: 1, color: 'success.main' }}>
              Wins (Successful)
            </Typography>
            <EffortsTable
              filteredQueries={winsData.queries}
              filteredExternalEfforts={winsData.externalEfforts}
              onRemoveExternalEffort={null} // No removal in read-only view
              statusType="wins"
              showActions={false} // Hide Query/Article buttons for cleaner report view
              allowDateEdit={false} // Disable date editing in read-only report view
            />
          </Box>
        )}

        {/* PR Efforts Table - Only show if there are efforts */}
        {effortsData.hasData && (
          <Box sx={{ mt: 3, px: { xs: 2, sm: 6 } }}>
            <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
              PR Efforts (In Progress)
            </Typography>
            <EffortsTable
              filteredQueries={effortsData.queries}
              filteredExternalEfforts={effortsData.externalEfforts}
              onRemoveExternalEffort={null} // No removal in read-only view
              statusType="inProgress"
              showActions={false} // Hide Query/Article buttons for cleaner report view
              allowDateEdit={false} // Disable date editing in read-only report view
            />
          </Box>
        )}
      </Card>

      {/* Notes Section */}
      {!isExpert && (
        <NotesSection
          companyName={apiCompanyName}
          startDate={startDate}
          endDate={endDate}
          allReports={allReports}
          allReportsMutate={allReportsMutate}
          decodedToken={decodedToken}
          isReportsLoading={isLoading}
        />
      )}
    </PageTemplate>
  );
};

export default ReportProfile;
